import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import type { Root, RootContent, Paragraph, Table, TableRow, TableCell } from "mdast";
import type { AppLanguageType } from "@noa/i18n";
import { formatLocaleDate } from "./date";

export type CellContent = {
  text: string;
  imageUrls: string[];
};

export type TimelineTableItem = {
  title: string;
  titleType?: "date" | "id";
  titleLink?: string;
  cardDetailedText?: CellContent[];
};

export type MarkdownSegment =
  | { type: "markdown"; content: string }
  | { type: "carousel"; slides: string[] }
  | { type: "timeline"; items: TimelineTableItem[]; title?: string }
  | { type: "action"; actionType: string; label: string; data: Record<string, unknown> };

export type MarkdownImage = { url: string; alt?: string };

export type MarkdownPart = { type: "markdown"; content: string } | { type: "images"; images: MarkdownImage[] };

interface NormalBufferItem {
  type: "normal";
  blocks: RootContent[];
}

interface SlideBufferItem {
  type: "slide";
  blocks: RootContent[];
}

interface TimelineBufferItem {
  type: "timeline";
  items: TimelineTableItem[];
  title?: string;
}

interface ActionBufferItem {
  type: "action";
  actionType: string;
  label: string;
  data: Record<string, unknown>;
}
type BufferedItem = NormalBufferItem | SlideBufferItem | TimelineBufferItem | ActionBufferItem;

const PROCESSOR = unified().use(remarkParse).use(remarkGfm);

const DATE_LIKE = /^\d{4}-\d{2}-\d{2}$/;
// Matches IDs/codes with at least 1 hyphen and only alphanumeric segments, e.g. "CV-003" or "2000-CV-003-02"
const FILE_ID_LIKE = /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)+$/;

function extractCellText(cell: TableCell): string {
  const parts: string[] = [];
  const visit = (nodes: unknown[]): void => {
    for (const n of nodes) {
      const node = n as Record<string, unknown>;
      if (typeof node.value === "string") parts.push(node.value);
      if (Array.isArray(node.children)) visit(node.children);
    }
  };
  visit(cell.children);
  return parts.join("");
}

function extractCellLink(cell: TableCell): string | null {
  const visit = (nodes: unknown[]): string | null => {
    for (const n of nodes) {
      const node = n as Record<string, unknown>;
      if (node.type === "link" && typeof node.url === "string") return node.url;
      if (Array.isArray(node.children)) {
        const found = visit(node.children);
        if (found) return found;
      }
    }
    return null;
  };
  return visit(cell.children);
}

function extractCellImageUrls(cell: TableCell): string[] {
  const urls: string[] = [];
  const visit = (nodes: unknown[]): void => {
    for (const n of nodes) {
      const node = n as Record<string, unknown>;
      if (node.type === "image" && typeof node.url === "string") urls.push(node.url);
      if (Array.isArray(node.children)) visit(node.children);
    }
  };
  visit(cell.children);
  return urls;
}

function getDateColumnIndex(table: Table): number {
  const rows = table.children as TableRow[];
  const bodyRows = rows.slice(1);

  if (bodyRows.length <= 3) return -1;

  const colCount = (rows[0]?.children as TableCell[])?.length ?? 0;

  for (let col = 0; col < colCount; col++) {
    const dateCount = bodyRows.filter((row) => {
      const cell = (row.children as TableCell[])[col];
      return cell ? DATE_LIKE.test(extractCellText(cell).trim()) : false;
    }).length;

    if (dateCount > bodyRows.length / 2) return col;
  }

  return -1;
}

function getFileIdColumnIndex(table: Table): number {
  const rows = table.children as TableRow[];
  const bodyRows = rows.slice(1);

  if (bodyRows.length <= 3) return -1;

  const colCount = (rows[0]?.children as TableCell[])?.length ?? 0;

  for (let col = 0; col < colCount; col++) {
    const fileIdCount = bodyRows.filter((row) => {
      const cell = (row.children as TableCell[])[col];

      if (!cell) return false;

      return extractCellLink(cell) !== null || FILE_ID_LIKE.test(extractCellText(cell).trim());
    }).length;

    if (fileIdCount > bodyRows.length / 2) return col;
  }

  return -1;
}

function tableToTimelineItems(table: Table, colIdx: number, colType: "date" | "id"): TimelineTableItem[] {
  const rows = table.children as TableRow[];
  const headers = (rows[0].children as TableCell[]).map(extractCellText);

  return rows.slice(1).map((row) => {
    const rawCells = row.children as TableCell[];
    const cells = rawCells.map((cell, i) => ({
      header: headers[i] ?? "",
      value: extractCellText(cell),
      link: extractCellLink(cell),
      imageUrls: extractCellImageUrls(cell),
    }));
    const item: TimelineTableItem = { title: cells[colIdx]?.value ?? "", titleType: colType };

    if (colType === "id" && cells[colIdx]?.link) {
      item.titleLink = cells[colIdx].link ?? undefined;
    }

    const others = cells.filter((_, i) => i !== colIdx).filter((c) => c.value.trim() || c.imageUrls.length > 0);

    if (others.length > 0) {
      item.cardDetailedText = others.map((c) => ({
        text: c.value.trim() ? (c.header ? `${c.header}: ${c.value.trim()}` : c.value.trim()) : c.header,
        imageUrls: c.imageUrls,
      }));
    }

    return item;
  });
}

function isImageBlock(node: RootContent): boolean {
  if (node.type !== "paragraph") return false;
  const p = node as Paragraph;
  return p.children.every((child) => child.type === "image" || (child.type === "text" && !child.value.trim()));
}

function extractNodeText(node: RootContent): string {
  const parts: string[] = [];
  const visit = (nodes: unknown[]): void => {
    nodes.forEach((n) => {
      const child = n as Record<string, unknown>;
      if (typeof child.value === "string") parts.push(child.value);
      if (Array.isArray(child.children)) visit(child.children);
    });
  };
  const record = node as unknown as Record<string, unknown>;

  if (Array.isArray(record.children)) visit(record.children as unknown[]);

  return parts.join("").trim();
}

function isTitleBlock(node: RootContent): boolean {
  if (node.type === "heading") return true;

  if (node.type !== "paragraph") return false;

  const p = node as Paragraph;
  const significant = p.children.filter((c) => !(c.type === "text" && !c.value.trim()));

  return significant.length === 1 && significant[0].type === "strong";
}

function sliceSource(source: string, blocks: RootContent[]): string {
  const start = blocks[0].position?.start.offset ?? 0;
  const end = blocks[blocks.length - 1].position?.end.offset ?? source.length;
  return source.slice(start, end).trim();
}

export function parseMarkdownIntoSegments(markdown: string): MarkdownSegment[] {
  if (!markdown.trim()) return [];

  const tree = PROCESSOR.parse(markdown) as Root;
  const blocks = tree.children;

  // Phase 1: walk blocks into normal/slide/timeline items
  const items: BufferedItem[] = [];
  let buffer: RootContent[] = [];

  const flush = () => {
    if (buffer.length > 0) {
      items.push({ type: "normal", blocks: buffer });
      buffer = [];
    }
  };

  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (block.type === "table") {
      const dateColIdx = getDateColumnIndex(block);

      if (dateColIdx >= 0) {
        const lastBlock = buffer[buffer.length - 1];
        const title = lastBlock && isTitleBlock(lastBlock) ? extractNodeText(lastBlock) : undefined;

        if (title !== undefined) buffer = buffer.slice(0, -1);

        flush();
        items.push({ type: "timeline", items: tableToTimelineItems(block, dateColIdx, "date"), title });
        i++;
        continue;
      }

      const fileIdColIdx = getFileIdColumnIndex(block);

      if (fileIdColIdx >= 0) {
        const lastBlock = buffer[buffer.length - 1];
        const title = lastBlock && isTitleBlock(lastBlock) ? extractNodeText(lastBlock) : undefined;
        if (title !== undefined) buffer = buffer.slice(0, -1);
        flush();
        items.push({ type: "timeline", items: tableToTimelineItems(block, fileIdColIdx, "id"), title });
        i++;
        continue;
      }
    }
    // Handle action code blocks
    if (block.type === "code") {
      const codeBlock = block as { lang?: string; value?: string };
      if (codeBlock.lang === "action" && codeBlock.value) {
        flush();
        try {
          const parsed = JSON.parse(codeBlock.value);
          if (
            typeof parsed.type === "string" &&
            typeof parsed.label === "string" &&
            typeof parsed.data === "object" &&
            parsed.data !== null &&
            !Array.isArray(parsed.data)
          ) {
            items.push({
              type: "action",
              actionType: parsed.type,
              label: parsed.label,
              data: parsed.data,
            });
          }
        } catch {
          // Invalid JSON — skip silently
        }
        i++;
        continue;
      }
    }
    if (isImageBlock(block)) {
      // At most one preceding text block goes with the image.
      // If that block is preceded by a heading, include the heading too.
      // Everything else in the buffer is flushed as normal content.
      let slideStart = 0;
      if (buffer.length > 0) {
        slideStart = buffer.length - 1;
        if (slideStart > 0 && buffer[slideStart - 1].type === "heading") {
          slideStart -= 1;
        }
      }
      // The very first paragraph in the message is the bot's intro summary — never include it in a slide.
      if (items.length === 0 && slideStart === 0 && buffer.length === 1 && buffer[0].type !== "heading") {
        slideStart = 1;
      }
      if (slideStart > 0) {
        items.push({ type: "normal", blocks: buffer.slice(0, slideStart) });
      }
      items.push({ type: "slide", blocks: [...buffer.slice(slideStart), block] });
      buffer = [];
      i++;
      continue;
    }
    if (block.type === "thematicBreak") {
      flush();
      items.push({ type: "normal", blocks: [block] });
    } else if (block.type === "heading") {
      flush();
      buffer.push(block);
    } else {
      buffer.push(block);
    }
    i++;
  }
  flush();

  // Phase 2: group consecutive slides (≥2) into carousels; demote singletons
  const segments: MarkdownSegment[] = [];
  let pendingSlides: SlideBufferItem[] = [];

  const flushPendingSlides = () => {
    if (pendingSlides.length === 0) return;
    if (pendingSlides.length >= 2) {
      segments.push({
        type: "carousel",
        slides: pendingSlides.map((s) => sliceSource(markdown, s.blocks)),
      });
    } else {
      segments.push({
        type: "markdown",
        content: sliceSource(markdown, pendingSlides[0].blocks),
      });
    }
    pendingSlides = [];
  };

  for (const item of items) {
    if (item.type === "slide") {
      pendingSlides.push(item as SlideBufferItem);
    } else if (item.type === "timeline") {
      flushPendingSlides();
      segments.push({ type: "timeline", items: item.items, title: item.title });
    } else if (item.type === "action") {
      flushPendingSlides();
      segments.push({
        type: "action",
        actionType: item.actionType,
        label: item.label,
        data: item.data,
      });
    } else {
      flushPendingSlides();
      segments.push({ type: "markdown", content: sliceSource(markdown, item.blocks) });
    }
  }
  flushPendingSlides();

  // Phase 3: merge consecutive markdown segments
  const merged: MarkdownSegment[] = [];
  for (const seg of segments) {
    const last = merged[merged.length - 1];
    if (seg.type === "markdown" && last?.type === "markdown") {
      last.content = `${last.content}\n\n${seg.content}`;
    } else {
      merged.push({ ...seg });
    }
  }
  return merged;
}

/**
 * Splits a markdown segment into text blocks and standalone image blocks.
 *
 * For renderers that cannot display a markdown image themselves and must render it as a real
 * component alongside the text — on mobile, `react-native-enriched-markdown` takes no request
 * headers, so an authenticated image left inline renders as blank space. Web needs none of this:
 * `react-markdown`'s `components` prop swaps in an authenticated `img`.
 *
 * Only *block* images are extracted — a paragraph holding nothing but images, the same
 * `isImageBlock` rule `parseMarkdownIntoSegments` uses to detect carousel slides. An image sitting
 * next to text (a BOM table cell, a sentence with an inline thumbnail) stays in the markdown: there
 * is nowhere to lift it to without destroying the surrounding flow.
 */
export function splitMarkdownImageBlocks(markdown: string): MarkdownPart[] {
  if (!markdown.trim()) return [];

  const tree = PROCESSOR.parse(markdown) as Root;
  const parts: MarkdownPart[] = [];
  let buffer: RootContent[] = [];

  const flush = () => {
    if (buffer.length === 0) return;
    const content = sliceSource(markdown, buffer);
    if (content) parts.push({ type: "markdown", content });
    buffer = [];
  };

  tree.children.forEach((block) => {
    const images = isImageBlock(block) ? blockImages(block) : [];

    // A whitespace-only paragraph also satisfies `isImageBlock`; without the length check it
    // would flush the buffer and emit an empty image row.
    if (images.length === 0) {
      buffer.push(block);
      return;
    }

    flush();
    parts.push({ type: "images", images });
  });
  flush();

  return parts;
}

function blockImages(block: RootContent): MarkdownImage[] {
  return (block as Paragraph).children
    .filter((child) => child.type === "image")
    .map((image) => ({ url: image.url, alt: image.alt || undefined }));
}

export function hasAtLeastOneCarousel(segments: MarkdownSegment[]): boolean {
  return segments.some((s) => s.type === "carousel");
}

export function hasAtLeastOneTimeline(segments: MarkdownSegment[]): boolean {
  return segments.some((s) => s.type === "timeline");
}

export function hasAtLeastOneAction(segments: MarkdownSegment[]): boolean {
  return segments.some((s) => s.type === "action");
}

export function getTimelineItemTitle(item: TimelineTableItem, appLanguage: AppLanguageType) {
  return item.titleType === "id" ? item.title : formatLocaleDate(item.title, appLanguage);
}
